import { useState, useEffect } from "react";
import {
    Layout, Input, InputNumber, Button, Table, Card, List, Space, Typography, Select, Modal, Form, Divider, Row, Col,
    message, Image,
} from "antd";
import { SearchOutlined, DeleteOutlined, MinusOutlined, PlusOutlined, UserAddOutlined, QrcodeOutlined } from "@ant-design/icons";
import { products, clients, type Product, type InvoiceItem } from "../../../data/mockData";
import { Products } from "../../../types/CreateProductType";


import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';

import useBillingShortcuts from '../../../hooks/useBillingShortcuts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
const { Sider, Content } = Layout;
const { Text } = Typography;
export type Client = {
    ledger_id: number;
    ledger_first_name: string;
    ledger_last_name?: string;
    ledger_mobile_no?: string;
    ledger_company_name?: string;
    ledger_gstin?: string;
    ledger_gst_no?: string;
};
const fetchProducts = async () => {
    const response = await apiRequest({ method: 'get', url: '/products' });
    return response.data;
};


const updateCartItem = async (sd_id: number, data: { quantity: number; mtr?: number }) => {
    return await apiRequest({ method: 'post', url: `/update-cart-item/${sd_id}`, data });
};

const submitSale = async (saleData: any) => {
    return await apiRequest({ method: 'post', url: '/submit-sale', data: saleData });
};
export default function Billing() {
    const { products, ledgers, loaded } = useAppSelector(state => state.master);
    // Search products
    const [search, setSearch] = useState("");
    const [sale_id, setsaleid] = useState(null);
    // Cart with per-item discount
    // Selected client or new client inputs
    // Total/bill discount
    const [totalDiscount, setTotalDiscount] = useState(0);

    const [extraDiscount, setExtraDiscount] = useState(0);

    // Filtered products
    const filteredProducts = products.filter((p) =>
        p.product_name.toLowerCase().includes(search.toLowerCase())
    );
    const [clients, setClients] = useState<Client[]>([]);
    const [cart, setCart] = useState<InvoiceItem[]>([]);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [customerName, setCustomerName] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [dropdownOptions, setDropdownOptions] = useState<{ label: string, value: number }[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [ledgerForm] = Form.useForm();
    const [couponCode, setCouponCode] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
    const [couponLoading, setCouponLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [transactionId, setTransactionId] = useState("");
    const [qrCodeUrl, setQrCodeUrl] = useState<string>("");

    const fetchCompanyQr = async () => {
        try {
            const res = await apiRequest({
                method: "get",
                url: "/get-company-setting",
            });
            const details = Array.isArray(res)
                ? res[0]
                : Array.isArray(res?.data)
                ? res.data[0]
                : res?.data || res || {};

            const qr =
                details.od_qr_code_url ||
                details.od_qr_code_link ||
                details.qr_code_link ||
                details.od_qr_code ||
                details.qr_code ||
                "";

            if (qr) {
                if (qr.startsWith("http://") || qr.startsWith("https://") || qr.startsWith("data:")) {
                    setQrCodeUrl(qr);
                } else {
                    const fileUrl = process.env.REACT_APP_FILE_URL || process.env.REACT_APP_API_URL || "";
                    const cleanFileUrl = fileUrl ? (fileUrl.endsWith("/") ? fileUrl : `${fileUrl}/`) : "/";
                    const cleanPath = qr.startsWith("/") ? qr.slice(1) : qr;
                    if (cleanPath.includes("uploads/")) {
                        setQrCodeUrl(`${cleanFileUrl}${cleanPath}`);
                    } else {
                        setQrCodeUrl(`${cleanFileUrl}uploads/organizations/logo/${cleanPath}`);
                    }
                }
            }
        } catch (err) {
            console.error("Failed to fetch company QR code:", err);
        }
    };

    useEffect(() => {
        fetchCompanyQr();
    }, []);

    const fetchLedger = async (mobile?: string) => {
        try {
            let url = `ledgers`;
            if (mobile) url += `?mobile=${encodeURIComponent(mobile)}`;

            const response = await apiRequest<{ data: Client[] }>({
                method: 'get',
                url,
            });

            const fetched = response.data || [];
            setClients(fetched);

            // Map to dropdown options with Company Name and GSTIN
            const options = fetched.map((ledger: any) => {
                const name = `${ledger.ledger_first_name} ${ledger.ledger_last_name || ''}`.trim();
                const company = ledger.ledger_company_name ? ` (${ledger.ledger_company_name})` : '';
                const gst = (ledger.ledger_gstin || ledger.ledger_gst_no) ? ` - GST: ${ledger.ledger_gstin || ledger.ledger_gst_no}` : '';
                return {
                    label: `${name}${company}${gst}`,
                    value: ledger.ledger_id,
                };
            });

            setDropdownOptions(options);

            // Auto-select if only one match
            if (options.length === 1) {
                const selected = fetched[0];
                setSelectedClient(selected);
                setCustomerName(`${selected.ledger_first_name} ${selected.ledger_last_name || ''}`);
                setCustomerPhone(selected.ledger_mobile_no || "");
            } else if (!mobile) {
                // Initial load: Default to "No Show" or Walk-in
                const noShow = fetched.find(l =>
                    l.ledger_first_name.toLowerCase().includes("no show") ||
                    l.ledger_first_name.toLowerCase().includes("walk-in")
                );
                if (noShow) {
                    setSelectedClient(noShow);
                    setCustomerName(`${noShow.ledger_first_name} ${noShow.ledger_last_name || ''}`);
                    setCustomerPhone(noShow.ledger_mobile_no || "");
                }
            }

        } catch (error) {
            showError('Failed to fetch Ledger');
            console.error(error);
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponCode) return;
        setCouponLoading(true);
        try {
            const res = await apiRequest({
                method: 'post',
                url: '/verify-coupon',
                data: {
                    coupon_code: couponCode,
                    sale_id: cart[0]?.sale_id,
                }
            });
            if (res && res.coupon_id) {
                setAppliedCoupon(res);
                showSuccess("Coupon applied!");
            } else {
                showError(res?.message || "Invalid coupon");
            }
        } catch (err) {
            showError("Coupon verification failed");
        } finally {
            setCouponLoading(false);
        }
    };

    const handleRemoveCoupon = async () => {
        try {
            const saleId = cart[0]?.sale_id;
            if (saleId) {
                await apiRequest({
                    method: 'post',
                    url: '/remove-coupon-from-sale',
                    data: { sale_id: saleId }
                });
            }
            setAppliedCoupon(null);
            setCouponCode("");
            showSuccess("Coupon removed successfully");
        } catch (err) {
            console.error(err);
            // Even if API fails, clear locally for better UX
            setAppliedCoupon(null);
            setCouponCode("");
            showSuccess("Coupon removed locally");
        }
    };

    const handleAddLedger = async (values: any) => {
        try {
            const payload = {
                ...values,
                ledger_company_name: values.ledger_company_name,
                ledger_gstin: values.ledger_gstin,
                ledger_gst_no: values.ledger_gstin,
            };
            const response = await apiRequest({
                method: 'post',
                url: '/add-ledger',
                data: payload,
            });

            if (response) {
                showSuccess("Ledger created successfully");
                setIsModalVisible(false);
                ledgerForm.resetFields();
                // Refresh and select the new ledger
                await fetchLedger(values.ledger_mobile_no);
            }
        } catch (error) {
            showError("Failed to create ledger");
            console.error(error);
        }
    };

    useEffect(() => {
        fetchLedger();
    }, []);
    // Add product to cart
    const addToCart = async (product: Products) => {
        if (!selectedClient) {
            showError("Please select a customer first!");
            return;
        }
        const existing = cart.find(i => i.product.product_id === product.product_id);
        if (existing) {
            await updateQty(existing.sd_id, 1);
            return;
        }

        const newItem = {
            sd_id: Date.now(),
            product,
            quantity: 1,
            product_price: product.product_discount_price,
            discount: 0,
            subtotal: product.product_discount_price,
        };

        setCart(prev => [...prev, newItem]);

        try {
            const DataLink = sale_id ? '/edit-to-cart/' + sale_id : '/add-to-cart';
            const DataMethod = sale_id ? 'put' : 'post';
            // Call API to create cart item
            const res = await apiRequest({
                method: DataMethod,
                url: DataLink,
                data: {
                    product_id: product.product_id,
                    ledger_id: selectedClient?.ledger_id || 1,
                    quantity: 1,
                    product_price: product.product_discount_price,
                },
            });
            console.log("res", res)
            setsaleid(res.sale_id)
            // Refresh cart from server
            refreshCart(res.sale_id);
        } catch (err) {
            showError("Failed to add item to server cart");
        }
    };
    const onInvoiceDownload = async (saleId: any) => {
        try {
            const response = await apiRequest<Blob>({
                method: 'post',
                url: '/download-invoice',
                data: {
                    sale_id: [saleId], // Still an array
                },
                config: {
                    responseType: 'blob',
                },
            });

            const blob = new Blob([response], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `tax_invoice_${saleId}.pdf`);
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

            message.success("Download started.");
        } catch (error) {
            console.error("Invoice download error:", error);
            message.error("Failed to download invoice.");
        }
    };
    const refreshCart = async (sId: any) => {
        try {
            const res = await apiRequest({
                method: 'post',
                url: `/get-to-cart/${sId}`,
            });
            if (res && res.data) {
                const mappedCart = res.data.map((item: any) => ({
                    sd_id: item.sd_id,
                    sale_id: item.sale_id,
                    product: {
                        product_id: item.sd_product_id,
                        product_name: item.product_name,
                        product_brand: item.brand_name,
                        product_mrp: item.sd_amount,
                        product_discount_price: item.sd_amount,
                        gst_name: item.gst_name || '0%',
                    },
                    quantity: item.sd_qty,
                    product_price: item.sd_amount,
                    discount: item.sd_discount,
                    subtotal: item.sd_sub_total,
                    gst_amount: item.sd_tax_amount || 0,
                    gst_name: item.gst_name || '0%',
                }));
                setCart(mappedCart);
            }
        } catch (err) {
            console.error("Refresh cart error:", err);
            // showError("Failed to refresh cart");
        }
    };
    const handleSubmitSale = async () => {
        if (!cart.length) {
            showError("Cart is empty!");
            return;
        }

        try {
            let saleId = cart[0].sale_id;
            const saleData = {
                ledger_id: selectedClient?.ledger_id,
                ledger_company_name: selectedClient?.ledger_company_name,
                ledger_gstin: selectedClient?.ledger_gstin || selectedClient?.ledger_gst_no,
                sale_id: saleId,
                payment_method: paymentMethod,
                transaction_id: transactionId
            };
            console.log("saleData", saleData)
            await submitSale(saleData);
            showSuccess("Sale submitted successfully!");
            
            // Reset for new sale
            setCart([]);
            setsaleid(null);
            setAppliedCoupon(null);
            setCouponCode("");
            setTransactionId("");
            setSelectedClient(null);
            setCustomerName("");
            setCustomerPhone("");
            
            onInvoiceDownload(saleId);
        } catch (err) {
            console.error(err);
            showError("Failed to submit sale");
        }
    };
    // Update quantity
    const updateQty = async (id: number, delta: number) => {
        const item = cart.find(i => i.sd_id === id);
        if (!item) return;

        const newQty = Math.max(0, item.quantity + delta);

        try {
            // Only call API if item has an sd_id
            if (item.sd_id) {
                await updateCartItem(item.sd_id, { quantity: newQty });
                if (sale_id) {
                    await refreshCart(sale_id);
                }
            }
        } catch (err) {
            console.error(err);
            showError("Failed to update item");
        }
    };

    // Update per-item discount
    const updateItemDiscount = (id: number, value: number) => {
        setCart((prev) =>
            prev.map((i) =>
                i.sd_id === id
                    ? { ...i, discount: value, subtotal: i.quantity * i.product_price - value }
                    : i
            )
        );
    };

    // Remove item
    const removeItem = async (id: number) => {
        try {
            await apiRequest({
                method: 'delete',
                url: `/delete-to-cart/${id}`,
            });
            showSuccess("Item removed");
            if (sale_id) {
                refreshCart(sale_id);
            } else {
                setCart((prev) => prev.filter((i) => i.sd_id !== id));
            }
        } catch (err) {
            showError("Failed to remove item");
        }
    };

    // Calculations
    const grossTotal = cart.reduce((acc, i) => acc + (i.quantity * i.product_price), 0);
    const perItemDiscountTotal = cart.reduce((acc, i) => acc + (i.discount || 0), 0);
    const couponDiscount = appliedCoupon?.coupon_amount || 0;
    const totalGst = cart.reduce((acc, i) => acc + (i.gst_amount || 0), 0);
    
    const standardDiscounts = perItemDiscountTotal + extraDiscount;
    const taxableAmount = grossTotal - standardDiscounts;
    const grandTotal = (taxableAmount + totalGst) - couponDiscount;

    // Table columns
    const columns = [
        {
            title: "Product",
            render: (_: any, record: InvoiceItem) => (
                <>
                    <div>{record.product.product_name}</div>
                    <Text type="secondary">{record.product.product_brand}</Text>
                </>
            ),
        },
        {
            title: "Qty",
            align: "center" as const,
            render: (_: any, record: InvoiceItem) => (
                <Space>
                    <Button
                        size="small"
                        icon={<MinusOutlined />}
                        onClick={() => updateQty(record.sd_id, -1)}
                    />
                    {record.quantity}
                    <Button
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => updateQty(record.sd_id, 1)}
                    />
                </Space>
            ),
        },
        {
            title: "Price",
            align: "right" as const,
            render: (_: any, record: InvoiceItem) => `Rs ${record.product_price}`,
        },
        {
            title: "Discount",
            align: "right" as const,
            render: (_: any, record: InvoiceItem) => (
                <InputNumber
                    min={0}
                    max={record.quantity * record.product_price}
                    value={record.discount}
                    onChange={(v) => updateItemDiscount(record.sd_id, v || 0)}
                    style={{ width: 80 }}
                />
            ),
        },
        {
            title: "GST",
            align: "right" as const,
            render: (_: any, record: InvoiceItem) => record.gst_name || '0%',
        },
        {
            title: "Subtotal",
            align: "right" as const,
            render: (_: any, record: InvoiceItem) => `Rs ${record.subtotal}`,
        },
        {
            title: "",
            render: (_: any, record: InvoiceItem) => (
                <Button
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => removeItem(record.sd_id)}
                />
            ),
        },
    ];

    return (
        <Layout style={{ height: "calc(100vh - 145px)", overflow: "hidden" }}>
            {/* Product List */}
            <Sider width={260} theme="light" style={{ borderRight: "1px solid #e1e7ef", display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: 12, borderBottom: "1px solid #e1e7ef" }}>
                    <Input
                        placeholder="Search product..."
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <List
                        className="product-item-parent"
                        dataSource={filteredProducts}
                        renderItem={(p) => (
                            <List.Item
                                className={`product-item ${!selectedClient ? 'disabled' : ''}`}
                                onClick={() => selectedClient && addToCart(p)}
                                style={{
                                    cursor: selectedClient ? "pointer" : "not-allowed",
                                    opacity: selectedClient ? 1 : 0.6
                                }}
                            >
                                <List.Item.Meta
                                    title={<span className="product-item-title">{p.product_name}</span>}
                                    description={p.brand_name}
                                />
                                <Text strong>
                                    ₹{p.product_discount_price < p.product_price
                                        ? p.product_discount_price
                                        : p.product_price}
                                </Text>

                                {p.product_discount_price < p.product_price && (
                                    <Text delete style={{ marginLeft: 8, color: '#999' }}>
                                        ₹{p.product_price}
                                    </Text>
                                )}
                                <Text strong>
                                    ₹{p?.gst_name}
                                </Text>
                            </List.Item>
                        )}
                    />
                </div>
            </Sider>

            {/* Cart */}
            <Content style={{ padding: 16, overflowY: 'auto' }}>
                <Card
                    className="sale-card"
                    style={{ borderRadius: 0, border: 0, padding: 0 }}
                    title={<span>{`Current Sale`}</span>}
                >
                    <Table rowKey="id" columns={columns} dataSource={cart} pagination={false} />
                </Card>
            </Content>

            {/* Summary Sidebar */}
            <Sider width={320} theme="light" style={{ borderLeft: "1px solid #e1e7ef", height: "100%", overflowY: 'auto' }}>
                <div style={{ padding: 16, height: "100%", display: "flex", flexDirection: "column" }}>

                    {/* Customer Section */}
                    <Card
                        title="Customer Details"
                        size="small"
                        extra={
                            <Button
                                type="link"
                                icon={<UserAddOutlined />}
                                size="small"
                                onClick={() => setIsModalVisible(true)}
                            >
                                Add New
                            </Button>
                        }
                        style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                    >
                        <Select
                            showSearch
                            placeholder="Search customer"
                            value={selectedClient?.ledger_id}
                            style={{ width: '100%', marginBottom: 8 }}
                            onChange={(val) => {
                                const client = clients.find(c => c.ledger_id === val);
                                if (client) {
                                    setSelectedClient(client);
                                    setCustomerName(`${client.ledger_first_name} ${client.ledger_last_name || ''}`);
                                    setCustomerPhone(client.ledger_mobile_no || "");
                                }
                            }}
                            options={dropdownOptions}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        />
                        <Input
                            placeholder="Enter phone to search"
                            value={customerPhone}
                            onChange={(e) => {
                                const val = e.target.value;
                                setCustomerPhone(val);
                                if (val.length >= 10) {
                                    fetchLedger(val);
                                }
                            }}
                        />

                        {selectedClient && (selectedClient.ledger_company_name || selectedClient.ledger_gstin || selectedClient.ledger_gst_no) && (
                            <div style={{ marginTop: 8, padding: '8px 10px', background: '#f8fafc', borderRadius: 6, fontSize: 12, border: '1px solid #e2e8f0' }}>
                                {selectedClient.ledger_company_name && (
                                    <div style={{ marginBottom: 2 }}>
                                        <Text type="secondary">Company: </Text>
                                        <Text strong>{selectedClient.ledger_company_name}</Text>
                                    </div>
                                )}
                                {(selectedClient.ledger_gstin || selectedClient.ledger_gst_no) && (
                                    <div>
                                        <Text type="secondary">GSTIN: </Text>
                                        <Text strong style={{ color: '#0958d9' }}>{selectedClient.ledger_gstin || selectedClient.ledger_gst_no}</Text>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>

                    {/* Totals Section */}
                    <Card title="Bill Summary" size="small" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <Text type="secondary">Subtotal (Gross)</Text>
                                <Text strong>Rs {grossTotal.toFixed(2)}</Text>
                            </div>

                            {(perItemDiscountTotal + extraDiscount > 0) && (
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <Text type="secondary">Discounts</Text>
                                    <Text type="danger">-Rs {(perItemDiscountTotal + extraDiscount).toFixed(2)}</Text>
                                </div>
                            )}

                            {appliedCoupon && (
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <Text type="success">Coupon ({appliedCoupon.coupon_code})</Text>
                                    <Text type="success">-Rs {appliedCoupon.coupon_amount.toFixed(2)}</Text>
                                </div>
                            )}

                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <Text type="secondary">Total GST</Text>
                                <Text strong>Rs {totalGst.toFixed(2)}</Text>
                            </div>

                            <Divider style={{ margin: '8px 0' }} />

                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                                <Text strong style={{ fontSize: 16 }}>Grand Total</Text>
                                <Text strong style={{ fontSize: 20, color: '#1890ff' }}>Rs {grandTotal.toFixed(2)}</Text>
                            </div>
                        </Space>
                    </Card>

                    {/* Coupon Section */}
                    <Card size="small" title="Apply Coupon" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Input
                                placeholder="Coupon Code"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                disabled={!!appliedCoupon || !selectedClient || cart.length === 0}
                            />
                            <Button
                                type="primary"
                                onClick={handleApplyCoupon}
                                loading={couponLoading}
                                disabled={!!appliedCoupon || !couponCode || !selectedClient || cart.length === 0}
                            >
                                Apply
                            </Button>
                        </Space.Compact>
                        {appliedCoupon && (
                            <Button
                                type="link"
                                danger
                                size="small"
                                onClick={handleRemoveCoupon}
                                style={{ padding: 0, marginTop: 4 }}
                            >
                                Remove Coupon
                            </Button>
                        )}
                    </Card>

                    {/* Payment Section */}
                    <div style={{ marginTop: 'auto' }}>
                        <Card size="small" title="Payment Method" style={{ marginBottom: 12 }}>
                            <Select
                                value={paymentMethod}
                                onChange={(val) => setPaymentMethod(val)}
                                style={{ width: '100%', marginBottom: 12 }}
                            >
                                <Select.Option value="Cash">Cash</Select.Option>
                                <Select.Option value="Card">Card</Select.Option>
                                <Select.Option value="UPI">UPI</Select.Option>
                            </Select>

                            {paymentMethod === 'Card' && (
                                <Input
                                    placeholder="Enter Card Ref/Trans ID"
                                    value={transactionId}
                                    onChange={(e) => setTransactionId(e.target.value)}
                                    style={{ marginBottom: 8 }}
                                />
                            )}

                            {paymentMethod === 'UPI' && (
                                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                                    <div style={{
                                        padding: 12,
                                        background: '#f8fafc',
                                        borderRadius: 8,
                                        marginBottom: 8,
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <Text type="secondary" style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: 12, color: '#475569' }}>
                                            SCAN QR CODE TO PAY
                                        </Text>

                                        {qrCodeUrl ? (
                                            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
                                                <Image
                                                    src={qrCodeUrl}
                                                    alt="UPI QR Code"
                                                    width={150}
                                                    height={150}
                                                    style={{
                                                        objectFit: "contain",
                                                        borderRadius: 8,
                                                        background: "#fff",
                                                        padding: 6,
                                                        border: "1px solid #cbd5e1",
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{
                                                width: 140,
                                                height: 140,
                                                margin: "0 auto 8px",
                                                background: "#f1f5f9",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                borderRadius: 8,
                                                border: "1px dashed #cbd5e1",
                                                color: "#64748b",
                                                fontSize: 12,
                                                padding: 8
                                            }}>
                                                <QrcodeOutlined style={{ fontSize: 32, marginBottom: 4, color: "#94a3b8" }} />
                                                <span>No QR code uploaded in Company Settings</span>
                                            </div>
                                        )}

                                        <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
                                            Amount: ₹{grandTotal.toFixed(2)}
                                        </div>
                                    </div>
                                    <Input
                                        placeholder="Enter UPI Trans ID / Ref ID"
                                        value={transactionId}
                                        onChange={(e) => setTransactionId(e.target.value)}
                                    />
                                </div>
                            )}
                        </Card>

                        <Button
                            type="primary"
                            block
                            size="large"
                            onClick={handleSubmitSale}
                            disabled={cart.length === 0}
                            style={{ height: 50, fontSize: 18, backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        >
                            Complete Sale
                        </Button>
                    </div>
                </div>
            </Sider>

            <Modal
                title="Add New Ledger / Customer"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => ledgerForm.submit()}
                okText="Create"
                width={800}
            >
                <Form
                    form={ledgerForm}
                    layout="vertical"
                    onFinish={handleAddLedger}
                >
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="ledger_first_name"
                                label="First Name"
                                rules={[{ required: true, message: 'Please enter first name' }]}
                                getValueFromEvent={(e) => e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1)}
                            >
                                <Input maxLength={50} showCount placeholder="First name" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="ledger_last_name"
                                label="Last Name"
                                getValueFromEvent={(e) => e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1)}
                            >
                                <Input maxLength={50} showCount placeholder="Last name" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="ledger_mobile_no"
                                label="Mobile Number"
                                rules={[
                                    { required: true, message: 'Please enter mobile number' },
                                    { len: 10, message: 'Mobile number must be exactly 10 digits' }
                                ]}
                                getValueFromEvent={(e) => {
                                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    return val;
                                }}
                            >
                                <Input maxLength={10} showCount placeholder="10-digit number" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="ledger_company_name"
                                label="Company Name"
                            >
                                <Input maxLength={100} showCount placeholder="e.g. ABC Enterprises" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="ledger_gstin"
                                label="GST Number (GSTIN)"
                                rules={[
                                    {
                                        pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                                        message: 'Please enter a valid 15-character GSTIN format',
                                    }
                                ]}
                                getValueFromEvent={(e) => e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')}
                            >
                                <Input maxLength={15} showCount placeholder="e.g. 24AAAAA0000A1Z5" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="ledger_billing_address"
                                label="Billing Address"
                            >
                                <Input.TextArea rows={2} maxLength={200} showCount placeholder="Enter billing address" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Layout>
    );
}